// Import Libraries
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Select, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';

// Import Services
import featuresService from '../../services/features';

// Import Components
import FeatureDrawer from '../components/feature/featureDrawer';

function AdminFeaturesPage() {
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isNewFeature, setIsNewFeature] = useState(false);

  // Fetch all features
  const fetchFeatures = async () => {
    const data = await featuresService.getAllFeatures();
    setFeatures(data || []);
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  // Handle clicking "New Feature"
  const handleCreateNew = () => {
    setSelectedFeature({ name: '', description: '', default_state: 'off', exceptions: [] });
    setIsNewFeature(true);
    setDrawerOpen(true);
  };

  // Handle clicking a row to edit an existing feature
  const handleRowClick = (params) => {
    setSelectedFeature(params.row);
    setIsNewFeature(false);
    setDrawerOpen(true);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'Feature Name', width: 200 },
    { field: 'description', headerName: 'Description', width: 300 },
    {
      field: 'default_state',
      headerName: 'Default State',
      width: 150,
      renderCell: (params) => (
        <div className="large" style={{ paddingTop: '10px', cursor: 'arrow' }}>
          {params.value ? (
            <FontAwesomeIcon icon={faToggleOn} className="purpleText" />
          ) : (
            <FontAwesomeIcon icon={faToggleOff} className="dkGrayText" />
          )}
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const handleChange = async (event) => {
          const selected = event.target.value;

          if (selected === 'delete') {
            const confirmed = window.confirm(`Delete feature "${params.row.name}"?`);
            if (confirmed) {
              try {
                await featuresService.deleteFeature(params.row.id);
                await fetchFeatures();
              } catch (error) {
                console.error('Failed to delete feature:', error);
              }
            }
          }
        };

        return (
          <Select size="small" defaultValue="" onChange={handleChange} displayEmpty fullWidth>
            <MenuItem value="">--</MenuItem>
            <MenuItem value="delete">Delete</MenuItem>
          </Select>
        );
      },
    },
  ];

  // Save feature (create or update)
  const handleSaveFeature = async () => {
    if (!selectedFeature?.name) return;

    const saved = await featuresService.upsertFeature({
      name: selectedFeature.name,
      description: selectedFeature.description,
      default_state: selectedFeature.default_state,
    });

    if (saved) {
      setDrawerOpen(false);
      setSelectedFeature(null);
      fetchFeatures();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Main Content */}
      <Box sx={{ flexGrow: 1, margin: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Feature Flags</Typography>
          <Button variant="contained" onClick={handleCreateNew}>
            New Feature
          </Button>
        </Box>

        <DataGrid
          rows={features}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
        />
      </Box>

      {/* Drawer Panel */}
      <FeatureDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        featureData={selectedFeature}
        setFeatureData={setSelectedFeature}
        onSave={handleSaveFeature}
        isNew={isNewFeature}
      />
    </Box>
  );
}

export default AdminFeaturesPage;
